<template>
  <div class="invoice-upload-content">
    <el-form :model="form" label-width="100px">
      <el-form-item label="账单月份" required>
        <el-date-picker
          v-model="form.month"
          type="month"
          placeholder="选择月份"
          format="YYYY-MM"
          value-format="YYYYMM"
        />
      </el-form-item>
      
      <el-form-item label="上传文件" required>
        <div 
          class="upload-area"
          @drop.prevent="handleFolderDrop"
          @dragover.prevent
          @dragenter.prevent="isDragging = true"
          @dragleave.prevent="isDragging = false"
          :class="{ 'is-dragging': isDragging }"
        >
          <div class="custom-upload-box" @click="triggerFileSelect">
            <el-icon class="el-icon--upload"><upload-filled /></el-icon>
            <div class="el-upload__text">将PDF文件或文件夹拖到此处，或<em>点击选择文件</em></div>
            <div class="el-upload__tip" style="color: #f56c6c;">PDF文件可批量拖拽，文件夹只能单个拖拽上传（最多20个文件）</div>
            <div class="el-upload__tip" style="color: #e6a23c; margin-top: 5px;">修改invoice文件名时，请勿增删_和-（下划线和连接符）</div>
          </div>
          <input 
            ref="fileInput" 
            type="file" 
            multiple 
            accept=".pdf"
            style="display: none" 
            @change="handleFileSelect"
          />
        </div>
        <div v-if="fileList.length > 0" class="file-list">
          <div class="file-count">已选择 {{ fileList.length }} 个文件</div>
          <div v-for="(file, index) in fileList" :key="index" class="file-item">
            <span class="file-path" :title="file.fullPath || file.name">{{ file.fullPath || file.name }}</span>
            <el-icon class="remove-icon" @click="removeFile(index)"><Close /></el-icon>
          </div>
        </div>
      </el-form-item>
    </el-form>
    
    <div class="dialog-footer">
      <el-button @click="$emit('cancel')">取消</el-button>
      <el-button type="primary" @click="submitUpload" :loading="uploading">
        开始上传并识别
      </el-button>
    </div>
  </div>
</template>

<script>
import { ref, reactive } from 'vue'
import { ElMessage } from 'element-plus'
import { UploadFilled, Close } from '@element-plus/icons-vue'

export default {
  name: 'InvoiceUploadContent',
  components: { UploadFilled, Close },
  emits: ['success', 'cancel'],
  setup(props, { emit }) {
    const uploading = ref(false)
    const uploadRef = ref(null)
    const fileInput = ref(null)
    const fileList = ref([])
    const isDragging = ref(false)
    
    const getDefaultMonth = () => {
      const now = new Date()
      const currentYear = now.getFullYear()
      const currentMonth = now.getMonth() + 1 // 1-12
      
      let lastYear = currentYear
      let lastMonth = currentMonth - 1
      
      if (lastMonth === 0) {
        lastYear = currentYear - 1
        lastMonth = 12
      }
      
      return lastYear + String(lastMonth).padStart(2, '0')
    }
    
    const form = reactive({
      month: getDefaultMonth()
    })
    
    const triggerFileSelect = () => {
      fileInput.value.click()
    }
    
    const handleFileSelect = (e) => {
      const files = Array.from(e.target.files)
      const pdfFiles = files.filter(file => file.name.toLowerCase().endsWith('.pdf'))
      
      if (pdfFiles.length > 20) {
        ElMessage.warning(`选择了 ${pdfFiles.length} 个PDF文件，超过20个文件限制，仅添加前20个`)
      }
      
      const filesToAdd = pdfFiles.slice(0, 20)
      filesToAdd.forEach(file => {
        fileList.value.push({
          name: file.name,
          fullPath: file.name,
          raw: file,
          uid: Date.now() + Math.random()
        })
      })
      
      if (filesToAdd.length > 0) {
        ElMessage.success(`已添加 ${filesToAdd.length} 个PDF文件`)
      }
      
      e.target.value = ''
    }
    
    const handleFolderDrop = async (e) => {
      isDragging.value = false
      const items = e.dataTransfer.items
      const files = e.dataTransfer.files
      
      let hasDirectory = false
      if (items && items.length > 0) {
        for (let i = 0; i < items.length; i++) {
          const item = items[i].webkitGetAsEntry()
          if (item && item.isDirectory) {
            hasDirectory = true
            break
          }
        }
      }
      
      if (!hasDirectory && files && files.length > 0) {
        handleDirectFileDrop(files)
        return
      }

      const pdfFiles = []
      
      const readEntry = async (entry, parentPath = '') => {
        if (entry.isFile) {
          return new Promise((resolve) => {
            entry.file((file) => {
              if (file.name.toLowerCase().endsWith('.pdf')) {
                const fullPath = parentPath ? `${parentPath}/${file.name}` : file.name
                pdfFiles.push({
                  file: file,
                  fullPath: fullPath
                })
              }
              resolve()
            })
          })
        } else if (entry.isDirectory) {
          const reader = entry.createReader()
          return new Promise((resolve) => {
            reader.readEntries(async (entries) => {
              const currentPath = parentPath ? `${parentPath}/${entry.name}` : entry.name
              for (const subEntry of entries) {
                await readEntry(subEntry, currentPath)
              }
              resolve()
            })
          })
        }
      }

      for (let i = 0; i < items.length; i++) {
        const item = items[i].webkitGetAsEntry()
        if (item) {
          await readEntry(item, '')
        }
      }

      if (pdfFiles.length > 0) {
        if (pdfFiles.length > 20) {
          ElMessage.warning(`识别到 ${pdfFiles.length} 个PDF文件，超过20个文件限制，仅添加前20个`)
        }
        
        const filesToAdd = pdfFiles.slice(0, 20)
        filesToAdd.forEach(item => {
          fileList.value.push({
            name: item.file.name,
            fullPath: item.fullPath,
            raw: item.file,
            uid: Date.now() + Math.random()
          })
        })
        
        ElMessage.success(`已添加 ${filesToAdd.length} 个PDF文件`)
      } else {
        ElMessage.warning('未找到PDF文件')
      }
    }
    
    const handleDirectFileDrop = (files) => {
      const pdfFiles = []
      Array.from(files).forEach(file => {
        if (file.name.toLowerCase().endsWith('.pdf')) {
          pdfFiles.push({
            file: file,
            fullPath: file.name
          })
        }
      })
      
      if (pdfFiles.length > 0) {
        if (pdfFiles.length > 20) {
          ElMessage.warning(`识别到 ${pdfFiles.length} 个PDF文件，超过20个文件限制，仅添加前20个`)
        }
        
        const filesToAdd = pdfFiles.slice(0, 20)
        filesToAdd.forEach(item => {
          fileList.value.push({
            name: item.file.name,
            fullPath: item.fullPath,
            raw: item.file,
            uid: Date.now() + Math.random()
          })
        })
        
        ElMessage.success(`已添加 ${filesToAdd.length} 个PDF文件`)
      } else {
        ElMessage.warning('未找到PDF文件')
      }
    }
    
    const removeFile = (index) => {
      fileList.value.splice(index, 1)
    }
    
    const handleFileChange = (file, files) => {
      if (files.length > 20) {
        ElMessage.warning('最多只能上传20个文件')
        fileList.value = files.slice(0, 20)
      } else {
        fileList.value = files
      }
    }
    
    const resetForm = () => {
      form.month = getDefaultMonth()
      fileList.value = []
      if (uploadRef.value) {
        uploadRef.value.clearFiles()
      }
    }
    
    const submitUpload = async () => {
      if (!form.month) {
        ElMessage.warning('请选择月份')
        return
      }
      
      if (fileList.value.length === 0) {
        ElMessage.warning('请选择文件')
        return
      }
      
      uploading.value = true
      try {
        const formData = new FormData()
        formData.append('month', form.month)
        // 添加过滤0费用数据参数
        const filterZero = localStorage.getItem('filterZeroData')
        formData.append('filter_zero', filterZero === 'true' ? '1' : '0')
        fileList.value.forEach(file => {
          formData.append('files', file.raw)
        })
        
        const response = await fetch('/api/bill/invoice/upload/', {
          method: 'POST',
          body: formData
        })
        
        const data = await response.json()
        if (data.status === 'success' || data.status === 'partial') {
          ElMessage.success(data.message)
          resetForm()
          emit('success')
        } else {
          ElMessage.error(data.message || '上传失败')
        }
      } catch (error) {
        console.error('上传失败:', error)
        ElMessage.error('上传失败，请重试')
      } finally {
        uploading.value = false
      }
    }
    
    return {
      form,
      fileList,
      uploading,
      uploadRef,
      fileInput,
      isDragging,
      triggerFileSelect,
      handleFileSelect,
      handleFolderDrop,
      removeFile,
      handleFileChange,
      submitUpload,
      resetForm
    }
  }
}
</script>

<style lang="scss" scoped>
.invoice-upload-content {
  .upload-area {
    position: relative;
    transition: all 0.3s ease;
    
    &.is-dragging {
      .custom-upload-box {
        border-color: #409eff;
        background-color: #ecf5ff;
      }
    }
    
    .custom-upload-box {
      width: 100%;
      height: 200px;
      border: 2px dashed #d9d9d9;
      border-radius: 6px;
      cursor: pointer;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      transition: all 0.3s ease;
      background: #fafafa;
      
      &:hover {
        border-color: #409eff;
        background-color: #ecf5ff;
      }
      
      .el-icon--upload {
        font-size: 67px;
        color: #c0c4cc;
        margin-bottom: 16px;
      }
      
      .el-upload__text {
        color: #606266;
        font-size: 14px;
        text-align: center;
        
        em {
          color: #409eff;
          font-style: normal;
        }
      }
      
      .el-upload__tip {
        font-size: 12px;
        color: #909399;
        margin-top: 7px;
        text-align: center;
      }
    }
  }
  
  .file-list {
    margin-top: 15px;
    max-height: 300px;
    overflow-y: auto;
    border: 1px solid #dcdfe6;
    border-radius: 4px;
    padding: 10px;
    
    .file-count {
      font-size: 13px;
      color: #909399;
      margin-bottom: 10px;
      padding: 0 4px;
      font-weight: 500;
    }
    
    .file-item {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 8px 12px;
      margin-bottom: 8px;
      background-color: #f5f7fa;
      border-radius: 4px;
      transition: all 0.3s ease;
      gap: 12px;
      
      &:hover {
        background-color: #ecf5ff;
        
        .remove-icon {
          opacity: 1;
        }
      }
      
      &:last-child {
        margin-bottom: 0;
      }
      
      .file-path {
        flex: 1;
        font-size: 13px;
        color: #606266;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        font-family: 'Consolas', 'Monaco', monospace;
      }
      
      .remove-icon {
        font-size: 16px;
        color: #f56c6c;
        cursor: pointer;
        opacity: 0.6;
        transition: opacity 0.3s ease;
        flex-shrink: 0;
        
        &:hover {
          opacity: 1;
        }
      }
    }
  }
  
  .dialog-footer {
    margin-top: 20px;
    text-align: right;
  }
}
</style>
